import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import { Button, FormControl, MenuItem, Select } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ConfirmDeleteDialog from './Modal/ConfirmDelete';
import TaskApiService from '../api/TaskApiService';
import TaskQueryApiService from '../api/TaskQueryApiService';
import { useNavigate } from 'react-router-dom';
import EditMenuButton from './Modal/EditMenuButton';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateTaskColumns, updateTaskFilters, updateTaskQuery } from '../store/correcttrackSlice';
import AuthUtil from './Utils/AuthUtil';
import { downloadCsv, toGridQuery } from '../utils/gridQuery';

const taskApiService = new TaskApiService();
const taskQueryService = new TaskQueryApiService();

// Built-in task columns translate via the i18n catalog; custom (org-defined) columns
// fall back to whatever name the admin saved on the server.
const STANDARD_COLUMN_KEYS = {
    id: 'common.id',
    shortDescription: 'fieldName.description',
    reportedUser: 'fieldName.reportedBy',
    reportedDate: 'fieldName.reportedOn',
    status: 'fieldName.status',
    priority: 'fieldName.priority',
    category: 'fieldName.category',
    project: 'fieldName.locationProject',
    assignedToUser: 'fieldName.assignedTo',
    lastUpdatedUser: 'fieldName.lastUpdatedUser',
    lastUpdatedDate: 'fieldName.lastUpdatedDate',
    tags: 'fieldName.tags',
    hazard: 'fieldName.hazard',
    organization: 'fieldName.organization',
};

// Built-in columns the server can compare as something other than text.
const COLUMN_TYPES = {
    id: 'number',
    reportedDate: 'date',
    lastUpdatedDate: 'date',
};

// A custom column's declared datatype, mapped to the grid type that offers the right filter
// operators. Anything not listed here — including booleans, whose stored representation isn't
// guaranteed — stays a text column and filters by matching rather than by range.
const CUSTOM_COLUMN_TYPES = {
    date: 'date',
    int: 'number',
    integer: 'number',
    number: 'number',
    decimal: 'number',
    float: 'number',
    double: 'number',
};

// Typing in the filter panel now costs a round trip, so hold off until the user pauses.
const FILTER_DEBOUNCE_MS = 400;

const Tasks = () => {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [columnHeaders, setColumnHeaders] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [sortModel, setSortModel] = useState([]);
    const currentQuery = useSelector((state) => state.correcttrack.taskQueryId);
    const [activeQueryId, setActiveQueryId] = useState(null);
    const [queries, setQueries] = useState([]);
    const [currentSelectedTask, setCurrentSelectedTask] = useState();
    const [currentFilters, setCurrentFilters] = useState(useSelector((state) => state.correcttrack.taskFilters));
    // The model the grid renders updates on every keystroke; the one below is what we query with.
    const [appliedFilters, setAppliedFilters] = useState(currentFilters);
    const [currentVisibleFields, setCurrentVisibleFields] = useState(useSelector((state) => state.correcttrack.taskColumns));

    const dispatch = useDispatch();
    const debounceRef = useRef(null);
    const userToken = useSelector((state) => state.correcttrack.userToken);

    const canCreateTasks = useMemo(() => {
        const isSuperAdmin = AuthUtil.isSuperAdmin(userToken);
        const isAdmin = AuthUtil.isAdmin(userToken);
        const projectUserXRefs = AuthUtil.getProjectUserXRefs(userToken);
        return (isSuperAdmin || isAdmin ||
            (projectUserXRefs && projectUserXRefs.some((permission) => permission.permissionLevel > 1)));
    }, [userToken]);

    const navigate = useNavigate();

    function CustomToolbar () {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                {/* Replaces GridToolbarExport, which would only ever see the loaded page. */}
                <Button
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    disabled={exporting || activeQueryId === null}
                    onClick={onExportClick}
                >
                    {t('common.export')}
                </Button>
            </GridToolbarContainer>
        );
    }

    function updateFilters (filterModel) {
        setCurrentFilters(filterModel);
        dispatch(updateTaskFilters(filterModel));

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setPage(0);
            setAppliedFilters(filterModel);
        }, FILTER_DEBOUNCE_MS);
    }

    function updateVisibleColumns (columnsModel) {
        setCurrentVisibleFields(columnsModel);
        dispatch(updateTaskColumns(columnsModel));
    }

    function updateSort (newSortModel) {
        setPage(0);
        setSortModel(newSortModel);
    }

    const buildQuery = useCallback(() => toGridQuery({
        page,
        pageSize,
        sortModel,
        filterModel: appliedFilters,
    }), [page, pageSize, sortModel, appliedFilters]);

    const populateTasks = useCallback(async (queryToUse) => {
        if (queryToUse === null || typeof queryToUse === 'undefined') {
            return;
        }

        setLoading(true);
        const response = await taskApiService.searchTasks(queryToUse, buildQuery());

        if (response != null) {
            setColumnHeaders(response.taskColumnDefinitions ?? []);
            setTasks(response.tasks?.items ?? []);
            setRowCount(response.tasks?.totalCount ?? 0);
        }

        setLoading(false);
    }, [buildQuery]);

    const onDeleteClick = async (value) => {
        if (value) {
            await taskApiService.deleteTask(currentSelectedTask.Id);
            // Removing a row shifts every later one into a different page, so re-read the page
            // from the server rather than splicing the local copy.
            await populateTasks(activeQueryId);
        }

        setShowDeleteDialog(false);
    };

    const onExportClick = async () => {
        setExporting(true);
        try {
            // The export endpoint ignores page/pageSize, so the file covers every row
            // matching the current filters rather than just what's on screen.
            const blob = await taskApiService.exportTasks(activeQueryId, buildQuery());
            if (blob) {
                downloadCsv(blob, 'tasks.csv');
            }
        } finally {
            setExporting(false);
        }
    };

    const columns = useMemo(() => {
        const columnsToSet = columnHeaders.map((header) => {
            const translationKey = STANDARD_COLUMN_KEYS[header.columnDefinition];
            // Standard columns translate via the catalog; custom columns use the per-locale
            // name resolved server-side from the column's NamesJson (falling back to source).
            const headerName = translationKey
                ? t(translationKey)
                : (header.localizedName ?? header.columnName);

            const column = {
                field: header.columnDefinition,
                headerName,
                flex: 1,
            };

            if (header.isCustomColumn) {
                // Custom column values arrive alongside the row keyed by column name. The
                // declared datatype decides which filter operators the panel offers — the
                // server compares the stored values the matching way.
                const customType = CUSTOM_COLUMN_TYPES[(header.columnDataType ?? '').toLowerCase()];
                const isDate = customType === 'date';
                const resolve = (params) => {
                    const value = resolveCustomValue(params.row, header.columnName);
                    return isDate ? formatStoredDate(value) : value;
                };
                column.renderCell = resolve;
                column.valueGetter = resolve;

                if (customType) {
                    column.type = customType;
                }
            } else if (COLUMN_TYPES[header.columnDefinition]) {
                column.type = COLUMN_TYPES[header.columnDefinition];
            }

            return column;
        });

        columnsToSet.push({
            field: 'Edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => (
                <EditMenuButton
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedTask}
                    params={params}
                    onEditFunc={() => {
                        navigate(`/Task/${params.row.id}`);
                    }}
                />
            ),
        });

        return columnsToSet;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnHeaders, t]);

    function resolveCustomValue (row, columnName) {
        if (row.customColumnData && row.customColumnData.length > 0) {
            return row.customColumnData.find((obj) => obj.columnName === columnName)?.columnValue ?? '';
        }
        return '';
    }

    // Dates are stored as ISO yyyy-MM-dd so they sort correctly in SQL; show them in the
    // viewer's own format. Parsed as parts rather than via Date(string), which would read a
    // date-only value as UTC and show the previous day west of Greenwich.
    function formatStoredDate (value) {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
        if (!match) {
            return value;
        }
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString();
    }

    async function populateQueries () {
        const taskQueries = await taskQueryService.getTaskQueries();
        const defaultQuery = await taskQueryService.getUserDefaultQuery();
        let userDefaultQueryId = 0;
        if(defaultQuery && defaultQuery.data && defaultQuery.data.queryId) {
            userDefaultQueryId = defaultQuery.data.queryId;
        }
        if(currentQuery === null || currentQuery === undefined || currentQuery === '') {
            dispatch(updateTaskQuery(userDefaultQueryId));
        } else {
            userDefaultQueryId = currentQuery;
        }
        if (taskQueries != null) {
            const queries = processQueries(taskQueries);
            setQueries(queries);
        }

        return userDefaultQueryId;
    }

    function processQueries (queries) {
        const processedQueries = [];
        processedQueries.push({value: t('tasksPage.defaultQuery'), id: 0})

        queries.forEach((query) => {
            const thisQuery = {};
            thisQuery.value = query.queryName;
            thisQuery.id = query.queryId;
            processedQueries.push(thisQuery);
        });
        return processedQueries;
    }

    useEffect(() => {
        void populateQueries().then((queryId) => setActiveQueryId(queryId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch whenever the query, page, sort or applied filters change.
    useEffect(() => {
        void populateTasks(activeQueryId);
    }, [activeQueryId, populateTasks]);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))', minHeight: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 2,
                }}
            >
                    <FormControl sx={{ minWidth: 250 }}>
                        <Select
                            value={currentQuery}
                            autoWidth
                            onChange={(newValue) => {
                                if (newValue.target.value > 0) {
                                    dispatch(updateTaskQuery(newValue.target.value));
                                    setPage(0);
                                    setActiveQueryId(newValue.target.value);
                                }
                            }
                            }
                        >
                            {queries?.map(option => {
                                return (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.value}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }}>
                        <Button size={'large'} variant="contained" disabled={!canCreateTasks} onClick={() => navigate('/Task/0')}>{t('tasksPage.createNewTask')}</Button>
                    </FormControl>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <DataGridPro
                        rows={tasks}
                        columns={columns}
                        loading={loading}
                        pagination
                        paginationMode="server"
                        sortingMode="server"
                        filterMode="server"
                        rowCount={rowCount}
                        page={page}
                        onPageChange={(newPage) => setPage(newPage)}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => {
                            setPage(0);
                            setPageSize(newPageSize);
                        }}
                        sortModel={sortModel}
                        onSortModelChange={updateSort}
                        getRowId={(row) => row.id}
                        components={{
                            Toolbar: CustomToolbar,
                        }}
                        filterModel={currentFilters}
                        onFilterModelChange={(newFilterModel) => updateFilters(newFilterModel)}
                        columnVisibilityModel={currentVisibleFields}
                        onColumnVisibilityModelChange={(newColumnsModel) => updateVisibleColumns(newColumnsModel)}
                        getRowHeight={() => 'auto'}
                        rowsPerPageOptions={[10, 25, 50, 100, 200]}
                    />
            </Box>

            <ConfirmDeleteDialog
                id="confirm-delete"
                keepMounted
                value={currentSelectedTask}
                open={showDeleteDialog}
                onClose={onDeleteClick}
            ></ConfirmDeleteDialog>
        </Box>
    );
};
export default Tasks;